import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import vtImg from "../../assets/vt.avif";
import v1 from "../../assets/v1.jpg";
import v2 from "../../assets/v2.jpg";
import v3 from "../../assets/v3.jpg";
import v4 from "../../assets/v4.jpg";
import v5 from "../../assets/v5.jpeg";
import v6 from "../../assets/v6.jpeg";

export default function VillaTheresa() {
  const heroImageRef = useRef(null);
  const rafRef = useRef(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  const galleryImages = [v1, v2, v3, v4, v5, v6];

  // Fade-in on scroll using IntersectionObserver
  useEffect(() => {
    const els = Array.from(document.querySelectorAll(".js-fade"));
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("visible"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Parallax (optimized transform only), stronger but clamped
  useEffect(() => {
    const imgEl = heroImageRef.current;
    if (!imgEl) return;

    const prefersReduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      imgEl.style.transform = "translateY(0)";
      return;
    }

    const factor = 0.05;
    const maxOffset = 48;

    const update = () => {
      rafRef.current = null;
      const rect = imgEl.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;
      const viewportCenter = window.innerHeight / 2;
      const distFromCenter = centerY - viewportCenter;
      let translateY = Math.round(distFromCenter * factor);
      if (translateY > maxOffset) translateY = maxOffset;
      if (translateY < -maxOffset) translateY = -maxOffset;
      imgEl.style.transform = `translateY(${translateY}px)`;
    };

    const onScroll = () => {
      if (!rafRef.current) {
        rafRef.current = window.requestAnimationFrame(update);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  // Modal keyboard handlers
  useEffect(() => {
    if (!isModalOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowRight") setModalIndex((i) => (i + 1) % galleryImages.length);
      if (e.key === "ArrowLeft") setModalIndex((i) => (i - 1 + galleryImages.length) % galleryImages.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isModalOpen, galleryImages.length]);

  const openModalAt = (idx) => {
    setModalIndex(idx);
    setModalOpen(true);
    // Keep scrolling enabled - don't lock body overflow
  };
  const closeModal = () => {
    setModalOpen(false);
  };

  return (
    <main
      className="vt-page white-bg"
      aria-labelledby="vt-title"
      style={{ minHeight: "100vh", paddingBottom: 64 }}
    >
      <style>{`
        :root{
          --primary: #eab308;
          --bg-white: #ffffff;
          --muted: #6b7280;
          --text: #0f172a;
          --card-bg: rgba(255,255,255,0.98);
          --glass: rgba(255,255,255,0.98);
          --radius: 14px;
          --container: 1200px;
        }

        /* Page background: plain white per request */
        .vt-page.white-bg {
          background: var(--bg-white);
          color: var(--text);
          -webkit-font-smoothing: antialiased;
          padding-top: 48px;
          padding-left: 18px;
          padding-right: 18px;
        }

        .vt-container {
          max-width: var(--container);
          margin: 0 auto;
          padding: 18px;
        }

        /* HERO layout */
        .vt-hero {
          display: grid;
          grid-template-columns: 1fr 420px;
          gap: 28px;
          align-items: center;
          margin-bottom: 22px;
        }

        .vt-ribbon { display:inline-flex; gap:10px; color:var(--primary); font-weight:700; margin-bottom:12px; align-items:center;}
        .vt-ribbon .dot { width:10px; height:10px; border-radius:50%; background:var(--primary); box-shadow: 0 6px 14px rgba(234,179,8,0.14); }

        .vt-title {
          font-family: Georgia, 'Times New Roman', serif;
          font-size: clamp(38px, 6vw, 72px);
          line-height: 1.2;
          margin: 0;
          color: var(--text);
        }

        .vt-underline {
          width: 160px;
          height: 6px;
          background: linear-gradient(90deg,var(--primary), rgba(234,179,8,0.7));
          border-radius: 6px;
          margin-top: 12px;
          margin-bottom: 14px;
          box-shadow: 0 8px 22px rgba(234,179,8,0.08);
        }

        .vt-subhead { color:var(--text); font-size:16.5px; line-height:1.6; margin-bottom:8px; }

        /* Image card */
        .vt-image-card {
          width: 100%;
          max-width: 420px;
          border-radius: 12px;
          overflow: hidden;
          background: var(--glass);
          padding: 6px;
          box-shadow: 0 18px 44px rgba(15,23,42,0.10);
          border: 1px solid rgba(234,179,8,0.06);
          transition: transform 260ms ease, box-shadow 260ms ease;
        }
        .vt-image-card:hover { transform: translateY(-6px); box-shadow: 0 26px 64px rgba(15,23,42,0.12); }

        .vt-image {
          width:100%;
          height: 320px;
          object-fit: cover;
          display:block;
          border-radius: 10px;
          will-change: transform;
        }

        .vt-image-caption { display:block; padding:8px 10px; font-size:13px; color:var(--muted); }

        /* Content - expanded main card with stronger shadow + highlight border */
        .vt-content { margin-top: 20px; }
        .vt-glass {
          background: var(--card-bg);
          border-radius: 12px;
          padding: 34px;
          box-shadow: 0 26px 90px rgba(15,23,42,0.12);
          border: 1px solid rgba(234,179,8,0.06);
          color: var(--text);
        }

        /* Section headings */
        .vt-section-hero { font-size: 28px; color: var(--text); margin: 0 0 10px 0; font-weight:700; }
        .vt-section-title { font-size: 20px; color: var(--text); margin: 0 0 8px 0; }

        .vt-paragraph { color:#374151; line-height:1.7; margin-bottom:12px; font-size:16.5px; }

        /* Mission & Vision gradients */
        .vt-callouts { display:grid; grid-template-columns: 1fr 1fr; gap:14px; margin-top:12px; }
        .vt-callout {
          border-radius: 10px;
          padding: 18px;
          color: #071025;
          box-shadow: 0 14px 36px rgba(2,6,23,0.06);
          border: 1px solid rgba(0,0,0,0.03);
        }
        .vt-callout--mission {
          background: linear-gradient(135deg, rgba(234,179,8,0.18), rgba(255,255,255,0.98));
          border-left: 6px solid rgba(234,179,8,0.95);
        }
        .vt-callout--vision {
          background: linear-gradient(135deg, rgba(99,102,241,0.06), rgba(255,255,255,0.98));
          border-left: 6px solid rgba(99,102,241,0.12);
        }
        .vt-callout h4 { margin: 0 0 8px 0; font-size: 16px; font-weight:700; }
        .vt-callout p { margin: 0; color: #425166; font-size:15px; font-weight:400; line-height:1.6; }

        /* Gallery prettier grid + hover zoom */
        .vt-gallery { 
          margin-top: 18px; 
          display: grid; 
          grid-template-columns: repeat(3, 1fr); 
          gap: 16px; 
        }
        .vt-thumb {
          border-radius: 12px;
          overflow: hidden;
          cursor: pointer;
          background: #fff;
          box-shadow: 0 8px 24px rgba(15,23,42,0.1);
          border: 1px solid rgba(234,179,8,0.1);
          transition: transform 260ms ease, box-shadow 260ms ease;
          display:flex;
        }
        .vt-thumb:hover { transform: translateY(-4px); box-shadow: 0 16px 40px rgba(15,23,42,0.15); }
        .vt-thumb-inner { position: relative; width:100%; height: 140px; overflow: hidden; }
        .vt-thumb img { width:100%; height:100%; object-fit: cover; display:block; transition: transform 420ms cubic-bezier(.2,.9,.38,1); }
        .vt-thumb:hover img { transform: scale(1.08); }

        /* Modal lightbox */
        .vt-modal { 
          position: fixed; 
          inset: 0; 
          display: flex; 
          align-items: center; 
          justify-content: center; 
          background: rgba(15, 23, 42, 0.75);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          z-index: 9999; 
          padding: 20px;
          overflow-y: auto;
        }
        .vt-modal-inner { 
          position: relative;
          width: 100%; 
          max-width: 1600px; 
          border-radius: 16px; 
          overflow: visible; 
          background: transparent;
          margin: auto;
          max-height: 95vh;
          display: flex;
          flex-direction: column;
        }
        .vt-modal-close { 
          position: absolute; 
          right: -50px; 
          top: 0; 
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px; 
          padding: 8px 12px; 
          cursor: pointer; 
          font-weight: 700;
          color: #fff;
          font-size: 18px;
          transition: all 0.2s ease;
          z-index: 10;
        }
        .vt-modal-close:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: scale(1.05);
        }
        .vt-modal img { 
          width: 100%; 
          height: auto; 
          max-height: 80vh;
          display: block; 
          object-fit: contain; 
          border-radius: 8px;
        }
        .vt-modal-footer { 
          display: flex; 
          justify-content: space-between; 
          align-items: center; 
          gap: 12px; 
          padding: 16px 20px; 
          background: rgba(15, 23, 42, 0.5);
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          border-top: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 0 0 16px 16px;
          color: #fff; 
          font-size: 14px;
          font-weight: 500;
        }

        /* Fade-in utility - faster animation */
        .js-fade { opacity: 0; transform: translateY(42px); transition: opacity 420ms cubic-bezier(.16,.9,.3,1), transform 420ms cubic-bezier(.16,.9,.3,1); will-change: opacity, transform; }
        .js-fade.visible { opacity:1; transform: translateY(0); }

        .map-embed {
          width: 100%;
          max-width: 900px;
          margin: 20px auto;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 26px 64px rgba(15, 23, 42, 0.12);
        }

        /* Mobile Optimizations */
        @media (max-width: 980px) {
          .vt-hero { grid-template-columns: 1fr; }
          .vt-image { height: 260px; }
          .vt-callouts { grid-template-columns: 1fr; }
          .vt-gallery { grid-template-columns: repeat(2, 1fr); }
          
          .vt-modal-close {
            right: 10px;
            top: 10px;
          }
        }

        @media (max-width: 768px) {
          .vt-page.white-bg {
            padding-left: 4px;
            padding-right: 4px;
          }
          
          .vt-container {
            padding: 4px;
          }
          
          .vt-glass {
            padding: 8px;
          }
          
          .vt-callout {
            padding: 8px;
          }
          
          .vt-title {
            text-align: center;
          }
          
          .vt-underline {
            margin-left: auto;
            margin-right: auto;
          }
          
          .vt-subhead {
            text-align: center;
          }
          
          .vt-section-hero,
          .vt-section-title {
            text-align: center;
          }
          
          .vt-paragraph {
            text-align: center;
          }
          
          .vt-callout h4 {
            text-align: center;
          }
          
          .vt-callout p {
            text-align: center;
          }
          
          .vt-image-card {
            margin: 0 auto;
          }
          
          .vt-gallery { 
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }
          
          .vt-thumb-inner {
            height: 120px;
          }
        }

        @media (max-width: 480px) {
          .vt-page.white-bg {
            padding-left: 2px;
            padding-right: 2px;
          }
          
          .vt-container {
            padding: 2px;
          }
          
          .vt-glass {
            padding: 6px;
          }
          
          .vt-callout {
            padding: 6px;
          }
          
          .vt-gallery { 
            grid-template-columns: repeat(3, 1fr);
            gap: 8px;
          }
          
          .vt-thumb-inner {
            height: 100px;
          }
        }

        /* Reduced motion */
        @media (prefers-reduced-motion: reduce) {
          .vt-image-card, .vt-thumb, .vt-thumb img, .js-fade { transition: none !important; transform: none !important; opacity: 1 !important; }
          .vt-modal-inner { animation: none !important; }
        }
      `}</style>

      <div className="vt-container">
        {/* HERO */}
        <section className="vt-hero">
          <div>
            <div className="vt-ribbon"></div>

            <h1 id="vt-title" className="vt-title js-fade" style={{ transitionDelay: "40ms" }}>
              Villa Theresa<br />High School
            </h1>

            <div className="vt-underline js-fade" style={{ transitionDelay: "120ms" }} />

            <p className="vt-subhead js-fade" style={{ transitionDelay: "180ms" }}>
              Founded in <strong>1932</strong> by the <strong>Franciscan Missionaries of Mary</strong>, Villa Theresa blends academic rigour with spiritual formation.
            </p>

            <p className="js-fade" style={{ color: "var(--muted)", marginTop: 8, transitionDelay: "220ms", textAlign: "center" }}>
              A warm, faith-driven community shaping young women who lead with humility and purpose.
            </p>
          </div>

          <div className="vt-image-card js-fade" style={{ transitionDelay: "260ms" }}>
            <img
              ref={heroImageRef}
              className="vt-image"
              src={vtImg}
              alt="Villa Theresa campus"
              loading="lazy"
              decoding="async"
            />
            <span className="vt-image-caption">Villa Theresa campus — heritage & modern learning</span>
          </div>
        </section>

        {/* MAIN CONTENT */}
        <section className="vt-content js-fade" style={{ transitionDelay: "320ms" }}>
          <article className="vt-glass" role="region" aria-labelledby="vt-about">
            <h2 id="vt-about" className="vt-section-hero">About & History</h2>

            <p className="vt-paragraph">
              Villa Theresa High School began in 1932 when the Franciscan Missionaries of Mary established a school focused on character,
              education and service. Over decades the school expanded thoughtfully, adding science and computer facilities and strengthening
              its curriculum under the ICSE board.
            </p>

            <p className="vt-paragraph">
              Today Villa Theresa is known in Mumbai for combining academic strength with spiritual formation and community engagement — students
              follow a balanced program of academics, practical labs, language development, and service opportunities.
            </p>

            {/* Mission & Vision */}
            <div style={{ marginTop: 8 }}>
              <h3 className="vt-section-title">Mission & Vision</h3>

              <div className="vt-callouts" style={{ marginTop: 12 }}>
                <div className="vt-callout vt-callout--mission js-fade" style={{ transitionDelay: "380ms" }}>
                  <h4 id="mission-title">Mission</h4>
                  <p>
                    To nurture young women who are intellectually competent, morally grounded and socially responsible — equipped with practical skills,
                    spiritual depth, and a heart for service.
                  </p>
                </div>

                <div className="vt-callout vt-callout--vision js-fade" style={{ transitionDelay: "420ms" }}>
                  <h4 id="vision-title">Vision</h4>
                  <p>
                    To create a compassionate learning community where each student blossoms into a confident leader who contributes to society with humility and integrity.
                  </p>
                </div>
              </div>
            </div>

            {/* Graduates list */}
            <div style={{ marginTop: 18 }}>
              <h3 className="vt-section-title">What Graduates Take With Them</h3>
              <ul style={{ marginTop: 10, paddingLeft: 18, color: "var(--text)", lineHeight: 1.75 }}>
                <li className="js-fade" style={{ transitionDelay: "460ms" }}>Franciscan values — humility, compassion, and service.</li>
                <li className="js-fade" style={{ transitionDelay: "500ms" }}>Strong moral and spiritual principles and a life of service.</li>
                <li className="js-fade" style={{ transitionDelay: "540ms" }}>Academic readiness and practical skills (labs, languages, digital literacy).</li>
                <li className="js-fade" style={{ transitionDelay: "580ms" }}>Leadership rooted in responsibility, empathy and integrity.</li>
              </ul>
            </div>

            {/* Gallery */}
            <div style={{ marginTop: 20 }}>
              <h3 className="vt-section-title">Gallery</h3>
              <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 6, textAlign: "center" }}>Tap or click any image to open.</p>

              <div className="vt-gallery" role="list" aria-label="Villa Theresa gallery">
                {galleryImages.map((src, idx) => (
                  <div
                    key={idx}
                    className="vt-thumb js-fade"
                    onClick={() => openModalAt(idx)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openModalAt(idx); }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open gallery image ${idx + 1}`}
                    style={{ transitionDelay: `${600 + idx * 40}ms` }}
                  >
                    <div className="vt-thumb-inner">
                      <img src={src} alt={`Villa Theresa ${idx + 1}`} loading="lazy" decoding="async" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <p style={{ marginTop: 18, color: "var(--muted)", fontSize: 14, textAlign: "center" }}>
              
            </p>

            {/* MAP SECTION */}
            <div className="js-fade" style={{ transitionDelay: "880ms", marginTop: 28 }}>
              <h3 className="vt-section-title">Location</h3>
              <p className="vt-paragraph">Find us on the map below:</p>
              <div className="map-embed">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3773.2134145841655!2d72.80457207489714!3d18.96617998221598!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7ce750037f375%3A0xde5e3926fa90d004!2sVilla%20Theresa%20High%20School!5e0!3m2!1sen!2sin!4v1760622556018!5m2!1sen!2sin"
                  width="100%"
                  height="400"
                  style={{ border: 0, borderRadius: 12, boxShadow: "0 18px 44px rgba(15,23,42,0.1)" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Google Maps location"
                ></iframe>
              </div>
            </div>
          </article>
        </section>
      </div>

      {/* Modal lightbox */}
      {isModalOpen && (
        <div className="vt-modal" role="dialog" aria-modal="true" aria-label="Image viewer" onClick={closeModal}>
          <div className="vt-modal-inner" onClick={(e) => e.stopPropagation()}>
            <button className="vt-modal-close" aria-label="Close viewer" onClick={closeModal}>✕</button>

            <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1, minHeight: 0, padding: '10px', background: 'transparent' }}>
              <button
                aria-label="Previous image"
                onClick={() => setModalIndex((i) => (i - 1 + galleryImages.length) % galleryImages.length)}
                style={{ 
                  background: "rgba(255, 255, 255, 0.1)", 
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)", 
                  fontSize: 32, 
                  padding: "12px 20px", 
                  cursor: "pointer", 
                  color: "#fff", 
                  flexShrink: 0, 
                  borderRadius: 12,
                  fontWeight: 300,
                  transition: "all 0.2s ease"
                }}
                onMouseOver={(e) => e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)"}
                onMouseOut={(e) => e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)"}
              >
                ‹
              </button>

              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
                <img
                  src={galleryImages[modalIndex]}
                  alt={`Villa Theresa large ${modalIndex + 1}`}
                  loading="eager"
                  style={{ width: "100%", maxHeight: "80vh", objectFit: "contain", display: "block", borderRadius: 12, boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5)" }}
                />
              </div>

              <button
                aria-label="Next image"
                onClick={() => setModalIndex((i) => (i + 1) % galleryImages.length)}
                style={{ 
                  background: "rgba(255, 255, 255, 0.1)", 
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)", 
                  fontSize: 32, 
                  padding: "12px 20px", 
                  cursor: "pointer", 
                  color: "#fff", 
                  flexShrink: 0, 
                  borderRadius: 12,
                  fontWeight: 300,
                  transition: "all 0.2s ease"
                }}
                onMouseOver={(e) => e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)"}
                onMouseOut={(e) => e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)"}
              >
                ›
              </button>
            </div>

            <div className="vt-modal-footer" style={{ justifyContent: "space-between" }}>
              <div style={{ color: "#fff", fontWeight: 600 }}>Image {modalIndex + 1} of {galleryImages.length}</div>
              <div style={{ display: "flex", gap: 8 }}>
                {galleryImages.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setModalIndex(i)}
                    aria-label={`Go to image ${i + 1}`}
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 10,
                      border: i === modalIndex ? "2px solid #eab308" : "2px solid transparent",
                      padding: 0,
                      background: i === modalIndex ? "#eab308" : "rgba(255, 255, 255, 0.4)",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      boxShadow: i === modalIndex ? "0 0 8px rgba(234, 179, 8, 0.6)" : "none"
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}